import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { EnvelopeFill } from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';
import './auth-shared.css';

interface ForgotPasswordFieldErrors
{
    email: string | null;
}

function validateFields(email: string): ForgotPasswordFieldErrors
{
    if (!email.trim())
    {
        return { email: 'Email is required' };
    }

    if (!/\S+@\S+\.\S+/.test(email))
    {
        return { email: 'Please enter a valid email address' };
    }

    return { email: null };
}

const ForgotPasswordNew: React.FC = () =>
{
    const navigate = useNavigate();
    const { forgotPassword } = useAuth();

    const [email, setEmail] = useState<string>('');
    const [sending, setSending] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [serverError, setServerError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const fieldErrors = validateFields(email);

    function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>): void
    {
        if (e.target === e.currentTarget)
        {
            navigate('/login');
        }
    }

    async function handleSubmit(e: React.FormEvent): Promise<void>
    {
        e.preventDefault();
        setSubmitted(true);
        setServerError(null);
        setSuccessMessage(null);

        if (fieldErrors.email)
        {
            return;
        }

        setSending(true);

        try
        {
            const response = await forgotPassword(email.trim());
            setSuccessMessage(response.message);
        }
        catch (err: any)
        {
            const message =
                err.response?.data?.detail ||
                'Failed to send the reset email. Please try again.';
            setServerError(message);
        }
        finally
        {
            setSending(false);
        }
    }

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void
    {
        setEmail(e.target.value);

        if (serverError)
        {
            setServerError(null);
        }

        if (successMessage)
        {
            setSuccessMessage(null);
        }
    }

    return (
        <div className="auth-page" onClick={handleBackdropClick}>
            <div className="auth-card forgot-card">
                <div className="text-center">
                    <div className="forgot-icon-wrapper" aria-hidden="true">
                        <EnvelopeFill size={28} />
                    </div>
                    <h2 className="auth-heading">Forgot Password?</h2>
                    <p className="auth-subtitle">
                        Enter your registered email address and we will email a temporary password if the account exists.
                    </p>
                </div>

                <div className="forgot-info-box">
                    Use the temporary password from the email to sign in. After login, you will be prompted to set a new permanent password.
                </div>

                {serverError && (
                    <div className="auth-server-error">
                        {serverError}
                    </div>
                )}

                {successMessage && (
                    <>
                        <div className="auth-server-success">
                            {successMessage}
                        </div>
                        <p className="forgot-success-hint">
                            Check your inbox and spam folder, then return to sign in with the temporary password.
                        </p>
                    </>
                )}

                <Form onSubmit={handleSubmit} noValidate>
                    <div className="auth-form-group">
                        <Form.Label className="auth-label">Registered Email</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            value={email}
                            onChange={handleInputChange}
                            placeholder="Enter your registered email"
                            required
                            isInvalid={submitted && !!fieldErrors.email}
                        />

                        {submitted && fieldErrors.email && (
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.email}
                            </Form.Control.Feedback>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="auth-submit-btn"
                        disabled={sending}
                    >
                        {sending
                            ? <Spinner as="span" animation="border" size="sm" />
                            : 'Send Email'
                        }
                    </button>

                    <p className="auth-footer-text">
                        Remember your password?
                        <Link to="/login" className="auth-footer-link">
                            {successMessage ? 'Go to Sign In' : 'Back to Sign In'}
                        </Link>
                    </p>
                </Form>
            </div>
        </div>
    );
};

export default ForgotPasswordNew;
